import Preview from "./Preview/Preview";


const ItemDataProps=[
    {
        name:"item",
        type:"Type",
        description:"Data item",
    },
    {
        name:"element",
        type:"HTMLElement|null",
        description:`
            HTMLElement associated with the item.
            @null if Element has not been created yet.
        `,
    },
],ItemData={
    name:"ItemData",
    type:"object",
    props:ItemDataProps,
},PopupProps=[
    {
        name:"horizontal",
        type:"boolean",
        description:`
            indicates that the flatlist items are going to be arranged horizontally in a row instead of vertically in a column
            @Setting the item element display to inline is required
        `,
    },
    {
        name:"EmptyComponent",
        type:"string | (props:{parent:HTMLElement})=>HTMLElement",
        description:`
            A component to show when the list is empty.
            @if a <b>string</b> is passed, a simple message is displayed.
        `,
    },
    {
        name:"backwards",
        type:"boolean",
        description:"make Flatlist scrollable from bottom to top / right to left",
        notice:`
            Make sure the FlatList behaves as expected, as a normal list before using the backwards prop.
            @Specify a flatlist height value if all elements get shown at once.
        `,
    },
    {
        name:"pagingEnabled",
        type:"boolean",
        notice:`
            Item element should have a specified height/width value (depending on the horizontal prop value)
            for the paging to function properly
        `,
    },
    {
        name:"scrollEnabled",
        type:"boolean",
        description:`
            When false, the flatlist cannot be scrolled via touch interaction.
            @default to true
        `,
    },
    {
        name:"threshold",
        type:"number",
        description:`
            A number in range 0..1.
            The visiblity fraction of an element in order to create the next one.
            @example, 0.5 => When half of the element is visible, the next one is created.
            @default 0.5
        `,
    },
    {
        name:"transition",
        type:"string",
        description:`
            Used with pagingEnabled true.
            @Specifies the transition animation from one element to the next
            @default to "ease 300ms"
        `,
    },
    {
        name:"renderItem",
        type:"(props:object):HTMLElement",
        required:true,
        description:`
            Function to execute on each data item
            @Must return an HTMLElement
        `,
        props:[
            {
                name:"props",
                type:"{parent:HTMLElement,item:Type,index:number,data:Type[]}",
                props:[
                    {
                        name:"parent",
                        type:"HTMLElement",
                        description:`
                            items container, not the flatlist actual element.
                            @accessible via flatlist.container
                        `,
                    },
                    /* {
                        name:"data",
                        type:"Type[]",
                        description:"the data array you passed as a prop, not the actual data array used by the flatlist",
                    }, */
                ],
            }
        ],
    },
    {
        name:"onSwipe",
        type:"(event:FlatListSwipeEvent):void",
        description:"Used with pagingEnabled true",
        props:[{
            name:"FlatListSwipeEvent",
            type:{
                name:"SwipeEvent",
                hash:"#vritra#useswipegesture",
            },
            props:[
                {name:"index",type:"number"},
                {
                    name:"container",type:"HTMLDivElement",
                    description:"same as flatlist.container",
                },
            ],
        }],
    },
    {
        name:"onAddItems",
        type:"(items:Type[]):void",
        description:"Triggered each time addItems method called",
        props:[
            {
                name:"items",
                type:"Type[]",
                description:"added items array",
            }
        ],
    },
    {
        name:"onRemoveItem",
        type:"(itemData:object):void",
        description:"Triggered each time removeItem method called",
        props:[
            {
                name:"itemData",
                type:"object",
                props:ItemDataProps,
            }
        ],
    },
    {
        name:"onFilled",
        type:"(data:object):void",
        description:"Called once the list element is filled",
        props:[{
            name:"data",type:"object",
            props:[...ItemData.props,{name:"index",type:"number"}],
        }],
    },
    {
        name:"onReachEnd",
        type:"(context:{container:HTMLElement,data:Type[]}):void",
        description:"triggered when the last data item is reached",
    },
],PopupParam={
    name:"props",
    type:"FlatListProps",
    description:"Popup Flatlist props object, used to overwrite the parent flatlist props",
},withElement={
    name:"withElement",
    type:"boolean",
    description:"if true removes the html element associated with the item, default to true",
},showItemsReturnValue={
    name:"Element",
    type:"FlatList",
    description:"The flatlist popup element if created, null otherwise",
},predicate={
    name:"predicate",
    type:"(item:Type,index:number,data:Type[]):boolean",
    description:"function used to find the item",
    props:[
        {
            name:"data",
            type:"Type[]",
            description:`
                this is the flatlist actual data array
                @mutating this array may lead to unexpected bahavior
            `,
        }
    ],
}

export default {
    id:"FlatList",
    typedynamic:true,
    parent:{name:"View"},
    preview:Preview,
    props:[
        ...["containerClassName","popupClassName"].map(name=>({name,type:"string"})),
        {
            name:"data",
            type:"Type[]",
            description:`
                Flatlist data array
                @Items should be unique
                @The flatlist doesn't use this array but its shallow copy
            `,
        },
        ...PopupProps,
    ],
    methods:[
        {
            name:"scrollToOffset",
            type:"(offset:number,smooth?:boolean):void",
            description:`
            Scrolls to a specific content pixel offset in the list
            @param smooth default to true
            `,
        },
        {
            name:"scrollToIndex",
            type:"(index:number,smooth?:boolean):void",
            description:"scrolls to item at index",
            props:[
                {
                    name:"smooth",
                    type:"boolean",
                    description:"Indicates if the scroll should be smooth, default to true",
                }
            ],
        },
        {
            name:"addItems",
            type:"(items:Type[]):void",
            description:"appends more data items to the data array",
        },
        {
            name:"removeItem",
            type:"function",
            overloads:[
                {
                    type:"(predicate:function,withElement:boolean):ItemData<Type>",
                    description:`
                        removeItem calls predicate once for each element of the data array, in ascending order, 
                        until it finds one where predicate returns true. If such item is found, it's removed.
                    `,
                    props:[predicate,withElement,ItemData],
                },
                {
                    type:"(item:any,withElement:boolean):ItemData<Type>",
                    props:[
                        {
                            name:"item",
                            description:"item to remove",
                        },
                        withElement,
                        ItemData,
                    ],
                },
            ],
            
        },
        {
            name:"showItems",
            type:"function",
            description:`
                Creates a flatlist on top of the original flatlist as a popup using the same props
                @<span>onReachEnd</span> and <span>onRemoveItem</span> props are not passed to the popup faltlist.
                @if items is not an array, The method removes the popup flatlist.
                @<b>returns</b> the popup flatlist element.
            `,
            returns:showItemsReturnValue,
            overloads:[
                {
                    type:"(items:Type[],props?:object):FlatList|null",
                    props:[PopupParam],
                },
                {
                    type:"(predicate:function,props?:object):FlatList|null",
                    props:[
                        {
                            ...predicate,
                            description:"function used to filter data items",
                        },
                        PopupParam,
                    ],
                    returns:showItemsReturnValue,
                },
            ],
        },
    ],
};
